import { ReactElement, useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import uniq from 'lodash/uniq';

import {
  DictionaryCustomFieldType,
  ICustomDictionaryItem,
  ICustomDictionaryType,
  IDictionaryTypeCustomField,
} from '~/services/api/dictionaries/types';
import Modal from '~/ui/components/common/Modal';
import Button from '~/ui/components/common/Button';

import { isRequiredCustomField } from '../helpers/dictionaryType';
import { getDefaultValues, getValidationResolver, IDictionaryFormValues } from '../form';

import AlternativeValues, { IAlternativeItem } from './AlternativeValues/AlternativeValues';

import styles from '../Dictionaries.module.scss';

interface IProps {
  hasCustomFields: boolean;
  hasAlternativeValues: boolean;
  dictionaryType: ICustomDictionaryType;
  dictionaryItem: ICustomDictionaryItem;
  handleDictionaryItemUpdateOrAdd: (item: ICustomDictionaryItem) => void;
  onClose: () => void;
  actionModalText: string;
  modalTitle: string;
}

const ModalForm = ({
  hasCustomFields,
  hasAlternativeValues,
  dictionaryType,
  dictionaryItem,
  handleDictionaryItemUpdateOrAdd,
  onClose,
  actionModalText,
  modalTitle,
}: IProps): ReactElement => {
  const customFields = hasCustomFields ? dictionaryType.customFields : [];
  const checkboxFields = customFields.filter(
    ({ type }) => type === DictionaryCustomFieldType.CHECKBOX,
  );
  const textFields = customFields.filter(({ type }) => type !== DictionaryCustomFieldType.CHECKBOX);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IDictionaryFormValues>({
    defaultValues: getDefaultValues(dictionaryItem, customFields),
    resolver: getValidationResolver(dictionaryType, textFields),
    mode: 'onChange',
  });

  const defaultAlternativeItems = useMemo(
    () =>
      dictionaryItem.alternativeValues?.reduce(
        (values: IAlternativeItem, item: string, index: number) => {
          values[`value${index + 1}`] = item;
          return values;
        },
        {},
      ),
    [dictionaryItem.alternativeValues],
  );

  const [alternativeItems, setAlternativeItems] =
    useState<IAlternativeItem>(defaultAlternativeItems);
  const [alternativeValues, setAlternativeValues] = useState<string[]>();

  const setItems = useCallback((value: { [x: string]: string }) => {
    setAlternativeValues(uniq(Object.values(value).filter(item => item.length)));
    setAlternativeItems(value);
  }, []);

  const isDesigned = !!dictionaryType.hasDesignedModal;
  const nameLabel = isDesigned ? `${dictionaryType.name} *` : 'Dictionary Name';

  const onSubmit = (values: IDictionaryFormValues): void => {
    handleDictionaryItemUpdateOrAdd({
      ...dictionaryItem,
      ...values,
      ...(alternativeValues && { alternativeValues }),
    });
  };

  const modalDesc = isDesigned
    ? null
    : dictionaryType.modalDescription ||
      `Please type a meaning${
        customFields.some(isRequiredCustomField) ? ' and an abbreviation' : ''
      } to define the “${dictionaryType.name}” dictionary`;

  const renderCustomField = ({ title, name: n, type }: IDictionaryTypeCustomField) => {
    if (type === DictionaryCustomFieldType.CHECKBOX) {
      return (
        <FormControl variant="outlined" fullWidth key={title}>
          <Controller
            name={n}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    color="primary"
                    onChange={e => field.onChange(e.target.checked)}
                  />
                }
                label={title}
              />
            )}
          />
        </FormControl>
      );
    }

    const isTextarea = type === DictionaryCustomFieldType.TEXTAREA;
    const fieldTitle = isDesigned ? title : `Dictionary ${title}`;
    const label = isRequiredCustomField({ title, name: n, type }) ? `${fieldTitle} *` : fieldTitle;
    const fieldError = errors[n]?.message;

    return (
      <FormControl
        variant="outlined"
        fullWidth
        size={isTextarea ? 'medium' : 'small'}
        key={title}
        error={!!fieldError}
      >
        <InputLabel shrink>{label}</InputLabel>
        <Controller
          name={n}
          control={control}
          render={({ field }) => (
            <OutlinedInput
              label={label}
              notched
              multiline={isTextarea}
              minRows={isTextarea ? 4 : 1}
              className={isTextarea ? styles.textareaField : undefined}
              name={field.name}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
            />
          )}
        />
        {!!fieldError && <FormHelperText>{fieldError}</FormHelperText>}
      </FormControl>
    );
  };

  return (
    <Modal open maxWidth="lg" className={styles.modalPaper} onClose={onClose}>
      <Box className={styles.innerContainer}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{modalTitle}</h3>
          {modalDesc && <p className={styles.description}>{modalDesc}</p>}
        </div>
        <div className={styles.modalBody}>
          <div className={styles.modalFields}>
            <FormControl variant="outlined" fullWidth size="small" error={!!errors.value}>
              <InputLabel shrink>{nameLabel}</InputLabel>
              <Controller
                name="value"
                control={control}
                render={({ field }) => (
                  <OutlinedInput
                    label={nameLabel}
                    notched
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                )}
              />
              {!!errors.value && <FormHelperText>{errors.value.message}</FormHelperText>}
            </FormControl>
            {textFields.map(renderCustomField)}
          </div>
          {checkboxFields.map(renderCustomField)}
          {hasAlternativeValues && (
            <AlternativeValues items={alternativeItems} setItems={setItems} />
          )}
        </div>
        <div className={styles.buttonsWrapper}>
          <Button color="primary" variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={Object.keys(errors).length > 0}
            onClick={handleSubmit(onSubmit)}
          >
            {actionModalText}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default ModalForm;
